import React from 'react'
import {
  Box,
  Button,
  Card,
  CardBody,
  Checkbox,
  CheckboxGroup,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  VStack,
} from '@chakra-ui/react'
import { SearchIcon } from '@chakra-ui/icons'
import { ResearchTag } from '../../../../types/Research'

interface ResearchFilterProps {
  tags: ResearchTag[]
  searchTerm: string
  selectedTags: string[]
  selectedLetter: string
  onSearchChange: (value: string) => void
  onTagsChange: (values: string[]) => void
  onLetterClick: (letter: string) => void
  children?: React.ReactNode
}

const ResearchFilter: React.FC<ResearchFilterProps> = ({
  tags,
  searchTerm,
  selectedTags,
  selectedLetter,
  onSearchChange,
  onTagsChange,
  onLetterClick,
  children,
}) => {
  return (
    <Grid templateColumns={{ base: '1fr', lg: '300px 1fr' }} gap={6}>
      {/* Left Sidebar - Tag Filters */}
      <GridItem as='aside'>
        <Card>
          <CardBody>
            <Heading size='sm' mb={4}>
              Filter by Topics
            </Heading>
            <CheckboxGroup value={selectedTags} onChange={onTagsChange}>
              <VStack align='start' spacing={2}>
                {tags.map((tag) => (
                  <Checkbox key={tag.id} value={tag.id}>
                    {tag.name}
                  </Checkbox>
                ))}
              </VStack>
            </CheckboxGroup>
            {selectedTags.length > 0 && (
              <Button
                size='xs'
                variant='ghost'
                mt={3}
                onClick={() => onTagsChange([])}
              >
                Clear Filters
              </Button>
            )}
          </CardBody>
        </Card>
      </GridItem>

      {/* Search and Letter Filter */}
      <GridItem as='section'>
        {/* Search Bar */}
        <InputGroup mb={4}>
          <InputLeftElement pointerEvents='none'>
            <SearchIcon color='gray.300' />
          </InputLeftElement>
          <Input
            placeholder='Search research by title, abstract, or tags...'
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </InputGroup>

        {/* A-Z Selector */}
        <Box mb={4}>
          <HStack spacing={1} flexWrap='wrap'>
            <Text fontSize='sm' color='gray.600' mr={2}>
              Filter by letter:
            </Text>
            {Array.from({ length: 26 }, (_, i) =>
              String.fromCharCode(65 + i)
            ).map((letter) => (
              <Button
                key={letter}
                size='xs'
                variant={selectedLetter === letter ? 'solid' : 'ghost'}
                colorScheme={selectedLetter === letter ? 'blue' : 'gray'}
                onClick={() => onLetterClick(letter)}
              >
                {letter}
              </Button>
            ))}
            {selectedLetter && (
              <Button
                size='xs'
                variant='ghost'
                onClick={() => onLetterClick('')}
              >
                Clear
              </Button>
            )}
          </HStack>
        </Box>

        {/* Research List / Children */}
        {children}
      </GridItem>
    </Grid>
  )
}

export default ResearchFilter
